// ═══════════════════════════════════════════════════════════════════════════════
// Plan Builder — Super Admin product configurator for B2B plans
//
// Features:
// - Dropdown plan selector with search + status filter (scales to many plans)
// - Draft system: new plans are local-only until explicitly saved
// - Unsaved changes tracking with dirty indicator
// - Discard drafts before saving; archive saved plans
// ═══════════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Plus, Copy, Archive, Check, X, Save, Search, Trash2, ChevronDown, Info,
  DollarSign, Sliders, ToggleLeft, Settings2, Sparkles,
  Infinity as InfinityIcon, Users, Map as MapIcon, HardDrive, Mail, Send, Layers, RotateCcw, Gift,
} from 'lucide-react';
import { cn } from '@/utils/helpers';
import { Spinner } from '@/components/shared/UIComponents';
import { FEATURE_META, APP_FEATURE_KEYS, PLATFORM_FEATURE_KEYS } from '@/lib/constants';
import {
  fetchPlans,
  createPlan,
  updatePlan,
  archivePlan,
  fetchPlanFeatureDefaults,
  upsertPlanFeatureDefaults,
  syncB2BPlanStripe,
} from '@/lib/api';
import { supabase } from '@/lib/supabase';
import { PricingAssistantPanel, type PricingChatContext, type ChatMessage } from '@/components/pricing-assistant/PricingAssistantPanel';
import type { TenantPlan } from '@/types';

// ── Types ────────────────────────────────────────────────────────────────────

type FeatureKey = string;
type EditorTab = 'general' | 'pricing' | 'features';
type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';
type StatusFilter = 'all' | 'active' | 'draft' | 'archived';

interface PlanDraft {
  display_name: string;
  name: string;
  description: string;
  color: string;
  rank: number;
  is_active: boolean;
  price_per_seat_monthly: number;
  price_per_seat_annual: number;
  stripe_price_id_monthly: string;
  stripe_price_id_annual: string;
  max_users: number | null;
  max_trips_per_month: number | null;
  max_storage_mb: number;
  max_campaigns_per_month: number;
  max_campaign_recipients: number;
  max_b2c_plans: number;
  max_b2c_free_plans: number;
}

interface DraftSnapshot {
  plan: TenantPlan;
  draft: PlanDraft;
  features: Record<string, boolean>;
  limits: Record<string, number | null>;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function slugify(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
}

function planToDraft(plan: TenantPlan): PlanDraft {
  return {
    display_name: plan.display_name, name: plan.name, description: plan.description,
    color: plan.color, rank: plan.rank, is_active: plan.is_active,
    price_per_seat_monthly: plan.price_per_seat_monthly,
    price_per_seat_annual: plan.price_per_seat_annual,
    stripe_price_id_monthly: plan.stripe_price_id_monthly,
    stripe_price_id_annual: plan.stripe_price_id_annual,
    max_users: plan.max_users, max_trips_per_month: plan.max_trips_per_month,
    max_storage_mb: plan.max_storage_mb,
    max_campaigns_per_month: plan.max_campaigns_per_month,
    max_campaign_recipients: plan.max_campaign_recipients,
    max_b2c_plans: plan.max_b2c_plans,
    max_b2c_free_plans: plan.max_b2c_free_plans,
  };
}

function emptyFeatures(): Record<string, boolean> {
  const m: Record<string, boolean> = {};
  for (const k of ALL_FEATURE_KEYS) m[k] = false;
  return m;
}

function emptyLimits(): Record<string, number | null> {
  const m: Record<string, number | null> = {};
  for (const k of ALL_FEATURE_KEYS) m[k] = null;
  return m;
}

const isDraftId = (id: string | null) => id?.startsWith('draft_') ?? false;

const ALL_FEATURE_KEYS = Object.keys(FEATURE_META);

function groupFeaturesByCategory(): Record<string, string[]> {
  const groups: Record<string, FeatureKey[]> = {};
  for (const key of ALL_FEATURE_KEYS) {
    const cat = FEATURE_META[key].category;
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(key);
  }
  return groups;
}

const FEATURE_GROUPS = groupFeaturesByCategory();
const CATEGORY_ORDER = Object.keys(FEATURE_GROUPS).sort();

const TABS: { key: EditorTab; label: string; icon: typeof Settings2 }[] = [
  { key: 'general',  label: 'General',          icon: Settings2 },
  { key: 'pricing',  label: 'Pricing & Limits',  icon: DollarSign },
  { key: 'features', label: 'Features',          icon: ToggleLeft },
];

// ═══════════════════════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════════════════════

export function PlanBuilder() {
  // ── Data State ──────────────────────────────────────────────────
  const [dbPlans, setDbPlans] = useState<TenantPlan[]>([]);
  const [localDraftIds, setLocalDraftIds] = useState<string[]>([]);
  const draftSnapshots = useRef<Map<string, DraftSnapshot>>(new Map());
  const [loading, setLoading] = useState(true);
  const [featureCounts, setFeatureCounts] = useState<Record<string, number>>({});

  // ── Editor State ────────────────────────────────────────────────
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<PlanDraft | null>(null);
  const [features, setFeatures] = useState<Record<string, boolean>>({});
  const [featureLimits, setFeatureLimits] = useState<Record<string, number | null>>({});
  const [tenantCount, setTenantCount] = useState<number>(0);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [activeTab, setActiveTab] = useState<EditorTab>('general');
  const [savedSnapshot, setSavedSnapshot] = useState<string | null>(null);

  // ── Selector State ──────────────────────────────────────────────
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [showCreate, setShowCreate] = useState(false);
  const [createName, setCreateName] = useState('');
  // Pricing Assistant — modal chat scoped to ONE plan at a time
  // (the currently selected draft). Built lazily on open: we fetch
  // the current plan's enabled features, map keys → human labels via
  // FEATURE_META, and hand that context to the modal as the seed for
  // the kickoff message.
  //
  // Conversation threads are keyed by plan.name so closing + reopening
  // (or switching plans) restores the prior chat without restarting
  // the conversation. Lives in this component (not the modal) so the
  // history survives unmount of the modal subtree.
  const [assistantOpen, setAssistantOpen]         = useState(false);
  const [assistantLoading, setAssistantLoading]   = useState(false);
  const [assistantContext, setAssistantContext]   = useState<PricingChatContext | null>(null);
  const [conversations, setConversations]         = useState<Record<string, ChatMessage[]>>({});
  const selectorRef = useRef<HTMLDivElement>(null);

  // ── Derived ─────────────────────────────────────────────────────
  const isCurrentDraft = isDraftId(selectedId);

  const isDirty = useMemo(() => {
    if (!draft) return false;
    if (isCurrentDraft) return true;
    if (!savedSnapshot) return false;
    return JSON.stringify({ d: draft, f: features, l: featureLimits }) !== savedSnapshot;
  }, [draft, features, featureLimits, savedSnapshot, isCurrentDraft]);

  const allPlans = useMemo(() => {
    const drafts = localDraftIds.map(id => draftSnapshots.current.get(id)?.plan).filter(Boolean) as TenantPlan[];
    return [...dbPlans, ...drafts];
  }, [dbPlans, localDraftIds]);

  const enabledCount = Object.values(features).filter(Boolean).length;
  const totalCount = ALL_FEATURE_KEYS.length;

  const counts = useMemo(() => ({
    all: allPlans.length,
    active: dbPlans.filter(p => p.is_active).length,
    draft: localDraftIds.length,
    archived: dbPlans.filter(p => !p.is_active).length,
  }), [allPlans, dbPlans, localDraftIds]);

  const filteredPlans = useMemo(() => {
    let list = allPlans;
    if (statusFilter === 'active') list = list.filter(p => p.is_active && !isDraftId(p.id));
    else if (statusFilter === 'archived') list = list.filter(p => !p.is_active && !isDraftId(p.id));
    else if (statusFilter === 'draft') list = list.filter(p => isDraftId(p.id));
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(p => p.display_name.toLowerCase().includes(q) || p.name.toLowerCase().includes(q));
    }
    return list;
  }, [allPlans, statusFilter, searchQuery]);

  const getFeatureCount = useCallback((planId: string) => {
    if (planId === selectedId) return enabledCount;
    if (isDraftId(planId)) {
      const snap = draftSnapshots.current.get(planId);
      return snap ? Object.values(snap.features).filter(Boolean).length : 0;
    }
    return featureCounts[planId] ?? 0;
  }, [selectedId, enabledCount, featureCounts]);

  // ── Click outside closes dropdown ─────────────────────────────
  useEffect(() => {
    if (!selectorOpen) return;
    const handler = (e: MouseEvent) => {
      if (selectorRef.current && !selectorRef.current.contains(e.target as Node)) {
        setSelectorOpen(false);
        setShowCreate(false);
        setCreateName('');
        setSearchQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [selectorOpen]);

  // ── Load Plans ────────────────────────────────────────────────
  const loadPlans = useCallback(async () => {
    try {
      const data = await fetchPlans();
      setDbPlans(data);
      const fc: Record<string, number> = {};
      await Promise.all(data.map(async (p) => {
        try { const f = await fetchPlanFeatureDefaults(p.id); fc[p.id] = f.filter(x => x.is_enabled).length; }
        catch { fc[p.id] = 0; }
      }));
      setFeatureCounts(fc);
    } catch (err) { console.error('Failed to load plans:', err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadPlans(); }, [loadPlans]);

  // ── Pricing Assistant — open + apply ──────────────────────────────
  // openAssistant builds context for the CURRENTLY SELECTED plan
  // only (single-plan focus per the chat model). It pulls the plan's
  // enabled feature keys, maps them to human-readable labels via
  // FEATURE_META (so the agent reads "AI Chatbots" instead of
  // "ai_chatbots"), and hands the bundle to the modal.
  //
  // Conversations are persisted in this component keyed by plan.name,
  // so closing the modal and reopening — or switching to a different
  // plan and back — restores the prior chat instead of restarting it.
  const openAssistant = useCallback(async () => {
    // Always open the modal so the button feels responsive. If no
    // plan is selected, the modal shows an empty-state ("Select a
    // plan in Plan Builder to start a pricing conversation") and
    // there's no context to fetch.
    setAssistantOpen(true);
    const selectedPlan = allPlans.find(p => p.id === selectedId);
    if (!selectedPlan) {
      setAssistantContext(null);
      return;
    }

    setAssistantLoading(true);
    try {
      // If this is a draft (not yet persisted) the plan_feature_defaults
      // row set won't exist in the DB yet — use the in-memory features
      // map instead so the agent can still reason about the draft.
      let enabledKeys: string[];
      if (isDraftId(selectedPlan.id)) {
        enabledKeys = Object.entries(features).filter(([, v]) => v).map(([k]) => k);
      } else {
        const rows = await fetchPlanFeatureDefaults(selectedPlan.id).catch(() => []);
        enabledKeys = rows.filter(r => r.is_enabled).map(r => r.feature_key);
      }

      const APP_KEYS_SET      = new Set(APP_FEATURE_KEYS);
      const PLATFORM_KEYS_SET = new Set(PLATFORM_FEATURE_KEYS);
      const b2cEnabled = enabledKeys.filter(k => APP_KEYS_SET.has(k)).length;
      const b2bEnabled = enabledKeys.filter(k => PLATFORM_KEYS_SET.has(k)).length;

      // Use the LIVE draft values when discussing the active draft —
      // that way the agent sees the admin's in-progress tweaks, not
      // the stale DB row.
      const liveMonthlyCents = draft?.price_per_seat_monthly ?? selectedPlan.price_per_seat_monthly;
      const liveAnnualCents  = draft?.price_per_seat_annual  ?? selectedPlan.price_per_seat_annual;
      const liveMaxUsers     = draft?.max_users      ?? selectedPlan.max_users;
      const liveStorageMb    = draft?.max_storage_mb ?? selectedPlan.max_storage_mb;

      // Map raw feature keys → human labels via FEATURE_META so the
      // agent's kickoff reads naturally.
      const featureLabels = enabledKeys.map(k => FEATURE_META[k]?.label ?? k);

      // Build the kickoff message inline — this surface frames it as
      // a platform B2B plan with seats + storage + B2C/B2B feature
      // split, which is the right framing for Plan Builder.
      const kickoff = buildPlatformKickoff({
        display_name:          selectedPlan.display_name,
        monthly_usd:           liveMonthlyCents / 100,
        annual_usd:            liveAnnualCents  / 100,
        max_users:             liveMaxUsers,
        storage_mb:            liveStorageMb,
        b2c_enabled:           b2cEnabled,
        b2c_total:             APP_FEATURE_KEYS.length,
        b2b_enabled:           b2bEnabled,
        b2b_total:             PLATFORM_FEATURE_KEYS.length,
        feature_labels:        featureLabels,
      });

      setAssistantContext({
        id:           selectedPlan.name,
        display_name: selectedPlan.display_name,
        kickoff,
      });
    } catch (err) {
      console.error('[PlanBuilder] Failed to build assistant context:', err);
    } finally {
      setAssistantLoading(false);
    }
  }, [allPlans, selectedId, features, draft]);

  // Apply pricing into the currently-active draft. monthly/annualCents
  // come from the modal already converted from USD → cents. Null on
  // either side means "leave the existing value alone" (the agent
  // returns null for Custom / Enterprise-style pricing).
  const handleAssistantApply = useCallback(
    (monthlyCents: number | null, annualCents: number | null) => {
      setDraft(prev => prev ? {
        ...prev,
        price_per_seat_monthly: monthlyCents !== null ? monthlyCents : prev.price_per_seat_monthly,
        price_per_seat_annual:  annualCents  !== null ? annualCents  : prev.price_per_seat_annual,
      } : prev);
    },
    [],
  );

  // Replace the conversation thread for the currently-discussed plan.
  // Used by the modal on every turn (append after send + after reply).
  const handleConversationChange = useCallback(
    (next: ChatMessage[]) => {
      if (!assistantContext) return;
      setConversations(prev => ({ ...prev, [assistantContext.id]: next }));
    },
    [assistantContext],
  );

  // "New chat" button — wipe the thread for the discussed plan so
  // the modal's kickoff effect fires again next render.
  const handleConversationReset = useCallback(() => {
    if (!assistantContext) return;
    setConversations(prev => {
      const next = { ...prev };
      delete next[assistantContext.id];
      return next;
    });
  }, [assistantContext]);

  // ── Persist current draft state before switching ──────────────
  const saveDraftState = useCallback(() => {
    if (selectedId && isDraftId(selectedId) && draft) {
      const existing = draftSnapshots.current.get(selectedId);
      if (existing) {
        draftSnapshots.current.set(selectedId, {
          plan: { ...existing.plan, ...draft },
          draft: { ...draft },
          features: { ...features },
          limits: { ...featureLimits },
        });
      }
    }
  }, [selectedId, draft, features, featureLimits]);

  // ── Select Existing Plan ──────────────────────────────────────
  const selectExistingPlan = useCallback(async (plan: TenantPlan) => {
    saveDraftState();
    setSelectedId(plan.id);
    setDraft(planToDraft(plan));
    setSaveStatus('idle');
    setActiveTab('general');
    try {
      const rows = await fetchPlanFeatureDefaults(plan.id);
      const map = emptyFeatures();
      const lim = emptyLimits();
      for (const r of rows) {
        if (r.feature_key in FEATURE_META) {
          map[r.feature_key] = r.is_enabled;
          lim[r.feature_key] = r.limit_value ?? null;
        }
      }
      setFeatures(map);
      setFeatureLimits(lim);
      setSavedSnapshot(JSON.stringify({ d: planToDraft(plan), f: map, l: lim }));
    } catch {
      const map = emptyFeatures();
      const lim = emptyLimits();
      setFeatures(map);
      setFeatureLimits(lim);
      setSavedSnapshot(JSON.stringify({ d: planToDraft(plan), f: map, l: lim }));
    }
    try {
      const { count } = await supabase.from('tenants').select('id', { count: 'exact', head: true }).eq('plan_id', plan.id);
      setTenantCount(count ?? 0);
    } catch { setTenantCount(0); }
  }, [saveDraftState]);

  // ── Select Draft Plan ──────────────────────────────────────────
  const selectDraftPlan = useCallback((id: string) => {
    saveDraftState();
    const snapshot = draftSnapshots.current.get(id);
    if (!snapshot) return;
    setSelectedId(id);
    setDraft({ ...snapshot.draft });
    setFeatures({ ...snapshot.features });
    setFeatureLimits({ ...(snapshot.limits || emptyLimits()) });
    setSavedSnapshot(null);
    setTenantCount(0);
    setSaveStatus('idle');
    setActiveTab('general');
  }, [saveDraftState]);

  // ── Select Plan (dispatch) ────────────────────────────────────
  const handleSelectPlan = useCallback((plan: TenantPlan) => {
    if (isDraftId(plan.id)) selectDraftPlan(plan.id);
    else selectExistingPlan(plan);
    setSelectorOpen(false);
    setSearchQuery('');
  }, [selectDraftPlan, selectExistingPlan]);

  // ── Create Draft ──────────────────────────────────────────────
  const handleCreateDraft = useCallback(() => {
    if (!createName.trim()) return;
    saveDraftState();
    const tempId = `draft_${Date.now()}`;
    const newPlan: TenantPlan = {
      id: tempId,
      name: slugify(createName),
      display_name: createName.trim(),
      description: '',
      color: '#64748b',
      rank: allPlans.length,
      is_active: true,
      price_per_seat_monthly: 0,
      price_per_seat_annual: 0,
      stripe_product_id: '',
      stripe_price_id_monthly: '',
      stripe_price_id_annual: '',
      max_users: null,
      max_trips_per_month: null,
      max_storage_mb: 1000,
      max_campaigns_per_month: 0,
      max_campaign_recipients: 0,
      max_b2c_plans: 4,
      max_b2c_free_plans: 1,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    const newDraft = planToDraft(newPlan);
    const newFeatures = emptyFeatures();
    const newLimits = emptyLimits();
    draftSnapshots.current.set(tempId, { plan: newPlan, draft: newDraft, features: newFeatures, limits: newLimits });
    setLocalDraftIds(prev => [...prev, tempId]);
    setSelectedId(tempId);
    setDraft(newDraft);
    setFeatures(newFeatures);
    setFeatureLimits(newLimits);
    setSavedSnapshot(null);
    setTenantCount(0);
    setSaveStatus('idle');
    setActiveTab('general');
    setCreateName('');
    setShowCreate(false);
    setSelectorOpen(false);
  }, [createName, allPlans.length, saveDraftState]);

  // ── Field Update ──────────────────────────────────────────────
  const upd = useCallback((patch: Partial<PlanDraft>) => {
    setDraft(prev => prev ? { ...prev, ...patch } : prev);
    setSaveStatus('idle');
  }, []);

  // ── Save ──────────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    if (!draft) return;
    setSaveStatus('saving');
    try {
      if (isCurrentDraft && selectedId) {
        const np = await createPlan({ ...draft });
        await upsertPlanFeatureDefaults(np.id, features, featureLimits);
        draftSnapshots.current.delete(selectedId);
        setLocalDraftIds(prev => prev.filter(id => id !== selectedId));
        setDbPlans(prev => [...prev, np].sort((a, b) => a.rank - b.rank));
        setSelectedId(np.id);
        setDraft(planToDraft(np));
        setFeatureCounts(prev => ({ ...prev, [np.id]: Object.values(features).filter(Boolean).length }));
        setSavedSnapshot(JSON.stringify({ d: planToDraft(np), f: features, l: featureLimits }));
      } else if (selectedId) {
        const updated = await updatePlan(selectedId, { ...draft });
        await upsertPlanFeatureDefaults(selectedId, features, featureLimits);
        setDbPlans(prev => prev.map(p => p.id === selectedId ? updated : p).sort((a, b) => a.rank - b.rank));
        setFeatureCounts(prev => ({ ...prev, [selectedId]: Object.values(features).filter(Boolean).length }));
        setSavedSnapshot(JSON.stringify({ d: draft, f: features, l: featureLimits }));
      }
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2500);
    } catch (err) {
      console.error('Save failed:', err);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  }, [selectedId, draft, features, featureLimits, isCurrentDraft]);

  // ── Discard / Revert ──────────────────────────────────────────
  const handleDiscard = useCallback(() => {
    if (!selectedId) return;
    if (isCurrentDraft) {
      draftSnapshots.current.delete(selectedId);
      setLocalDraftIds(prev => prev.filter(id => id !== selectedId));
      setSelectedId(null);
      setDraft(null);
      setFeatures({});
      setFeatureLimits({});
      setSaveStatus('idle');
    } else if (savedSnapshot) {
      const snap = JSON.parse(savedSnapshot);
      setDraft(snap.d);
      setFeatures(snap.f);
      setFeatureLimits(snap.l || emptyLimits());
      setSaveStatus('idle');
    }
  }, [selectedId, isCurrentDraft, savedSnapshot]);

  // ── Duplicate ─────────────────────────────────────────────────
  const handleDuplicate = useCallback(async () => {
    if (!draft) return;
    setSaveStatus('saving');
    try {
      const np = await createPlan({
        name: `${draft.name}_copy`, display_name: `${draft.display_name} (Copy)`,
        description: draft.description, color: draft.color, rank: draft.rank + 1,
        price_per_seat_monthly: draft.price_per_seat_monthly, price_per_seat_annual: draft.price_per_seat_annual,
        max_users: draft.max_users, max_trips_per_month: draft.max_trips_per_month,
        max_storage_mb: draft.max_storage_mb, max_campaigns_per_month: draft.max_campaigns_per_month,
        max_campaign_recipients: draft.max_campaign_recipients,
        max_b2c_plans: draft.max_b2c_plans, max_b2c_free_plans: draft.max_b2c_free_plans,
        stripe_price_id_monthly: draft.stripe_price_id_monthly, stripe_price_id_annual: draft.stripe_price_id_annual,
      });
      await upsertPlanFeatureDefaults(np.id, features, featureLimits);
      await loadPlans();
      selectExistingPlan(np);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2500);
    } catch (err) {
      console.error('Duplicate failed:', err);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  }, [draft, features, featureLimits, loadPlans, selectExistingPlan]);

  // ── Archive ───────────────────────────────────────────────────
  const handleArchive = useCallback(async () => {
    if (!selectedId || isCurrentDraft) return;
    if (tenantCount > 0) { alert('Cannot archive — this plan has active tenants. Reassign them first.'); return; }
    setSaveStatus('saving');
    try {
      await archivePlan(selectedId);
      setDbPlans(prev => prev.map(p => p.id === selectedId ? { ...p, is_active: false } : p));
      setDraft(prev => prev ? { ...prev, is_active: false } : prev);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2500);
    } catch (err) {
      console.error('Archive failed:', err);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  }, [selectedId, tenantCount, isCurrentDraft]);

  // ── Render ────────────────────────────────────────────────────

  if (loading) return <div className="flex items-center justify-center py-32"><Spinner size="lg" /></div>;

  const selectedPlan = allPlans.find(p => p.id === selectedId);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* ── Header ──────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-surface-900">Plan Builder</h1>
          <p className="text-surface-500 text-sm mt-0.5">Configure pricing, limits, and feature access for each plan</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {/* Pricing Assistant — always visible on PlanBuilder even
              before a plan is selected. Click pulls per-plan feature
              counts and opens a slide-out side panel that proposes
              monthly + annual prices grounded in travel-SaaS market
              data. Apply only operates on the currently selected
              draft (see PricingAssistantPanel). */}
          {/* Always clickable — the modal handles the "no plan
              selected yet" case with its own empty-state. Previously
              this button was disabled until a plan was selected,
              which read as "broken" to anyone landing on the page
              fresh. Now the click always opens the modal; if no
              plan is selected, the modal nudges the admin to pick
              one from the dropdown below. */}
          <button
            onClick={openAssistant}
            disabled={assistantLoading}
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border transition-all',
              'border-brand-500/40 text-brand-600 dark:text-brand-400 bg-brand-500/[0.06] hover:bg-brand-500/[0.12]',
              assistantLoading && 'opacity-60 cursor-not-allowed',
            )}
            title="Chat with the AI pricing assistant"
          >
            <Sparkles size={13} />
            {assistantLoading ? 'Loading…' : 'Pricing Assistant'}
          </button>
        {draft && (
          <>
            {saveStatus === 'saved' && (
              <span className="flex items-center gap-1.5 text-xs font-medium text-emerald-400 bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 rounded-lg px-2.5 py-1.5 animate-fade-in">
                <Check size={13} /> Saved
              </span>
            )}
            {saveStatus === 'error' && (
              <span className="flex items-center gap-1.5 text-xs font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-lg px-2.5 py-1.5">
                <X size={13} /> Failed
              </span>
            )}
            {isDirty && saveStatus === 'idle' && (
              <span className="flex items-center gap-1.5 text-xs font-medium text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 rounded-lg px-2.5 py-1.5">
                Unsaved changes
              </span>
            )}

            {isCurrentDraft ? (
              <button onClick={handleDiscard} className="btn-danger text-sm !py-2 !px-3.5">
                <Trash2 size={14} /> Discard Draft
              </button>
            ) : (
              <>
                {isDirty && savedSnapshot && (
                  <button onClick={handleDiscard} className="btn-ghost text-sm !py-2 !px-3">
                    <RotateCcw size={14} /> Undo Changes
                  </button>
                )}
                <button onClick={handleDuplicate} disabled={saveStatus === 'saving'} className="btn-secondary text-sm !py-2 !px-3.5">
                  <Copy size={14} /> Duplicate
                </button>
                <button onClick={handleArchive} disabled={saveStatus === 'saving' || tenantCount > 0}
                  title={tenantCount > 0 ? `Cannot archive — ${tenantCount} active tenant(s)` : 'Archive plan'}
                  className="btn-danger text-sm !py-2 !px-3.5">
                  <Archive size={14} /> Archive
                </button>
              </>
            )}
            <button onClick={handleSave} disabled={saveStatus === 'saving'}
              className={cn('btn-primary text-sm !py-2 !px-3.5', isDirty && 'shadow-glow')}>
              {saveStatus === 'saving' ? <Spinner size="sm" /> : <Save size={14} />}
              {isCurrentDraft ? 'Save to Database' : 'Save'}
            </button>
          </>
        )}
        </div>
      </div>

      {/* ── Plan Selector Dropdown ──────────────────────────────── */}
      <div className="relative" ref={selectorRef}>
        <button
          onClick={() => setSelectorOpen(!selectorOpen)}
          className={cn(
            'flex items-center gap-3 w-full max-w-sm px-4 py-3 rounded-xl border transition-all text-left',
            selectorOpen
              ? 'bg-surface-0 border-brand-500/40 shadow-glow-sm'
              : 'bg-surface-0 border-overlay/[0.06] hover:border-overlay/[0.12]',
          )}
        >
          {selectedPlan ? (
            <>
              <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: selectedPlan.color || '#64748b' }} />
              <span className="flex-1 text-sm font-medium text-surface-900 truncate">{selectedPlan.display_name}</span>
              {isDraftId(selectedPlan.id) && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-amber-50 dark:bg-amber-500/15 text-amber-600 dark:text-amber-400 font-medium">Draft</span>
              )}
              {!selectedPlan.is_active && !isDraftId(selectedPlan.id) && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-surface-300/50 text-surface-500 font-medium">Archived</span>
              )}
            </>
          ) : (
            <span className="flex-1 text-sm text-surface-500">Select a plan…</span>
          )}
          <ChevronDown size={16} className={cn('text-surface-500 transition-transform', selectorOpen && 'rotate-180')} />
        </button>

        {selectorOpen && (
          <div className="absolute z-50 top-full mt-2 w-full max-w-sm glass-panel-solid shadow-elevated border border-overlay/[0.06] overflow-hidden animate-scale-in">
            {/* Search + filters */}
            <div className="p-3 border-b border-overlay/[0.04]">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-500" />
                <input
                  type="text"
                  placeholder="Search plans…"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm bg-surface-50/50 border border-overlay/[0.06] rounded-lg text-surface-900 placeholder-surface-500 focus:outline-none focus:border-brand-500/40 focus:ring-2 focus:ring-brand-500/20"
                  autoFocus
                />
              </div>
              <div className="flex gap-1 mt-2">
                {(['all', 'active', 'draft', 'archived'] as StatusFilter[]).map(f => (
                  <button
                    key={f}
                    onClick={() => setStatusFilter(f)}
                    className={cn(
                      'text-[10px] font-medium px-2 py-1 rounded-md transition-colors capitalize',
                      statusFilter === f
                        ? 'bg-brand-500/15 text-brand-600 dark:text-brand-400'
                        : 'text-surface-500 hover:text-surface-700 hover:bg-overlay/[0.04]',
                    )}
                  >
                    {f} ({counts[f]})
                  </button>
                ))}
              </div>
            </div>

            {/* Plan List */}
            <div className="max-h-64 overflow-y-auto py-1">
              {filteredPlans.length === 0 ? (
                <div className="px-4 py-6 text-center text-xs text-surface-500">No plans match your filters</div>
              ) : (
                filteredPlans.map(plan => {
                  const sel = plan.id === selectedId;
                  const fc = getFeatureCount(plan.id);
                  return (
                    <button
                      key={plan.id}
                      onClick={() => handleSelectPlan(plan)}
                      className={cn(
                        'flex items-center gap-3 w-full px-4 py-2.5 text-left transition-colors',
                        sel ? 'bg-brand-500/10' : 'hover:bg-overlay/[0.04]',
                      )}
                    >
                      <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: plan.color || '#64748b' }} />
                      <span className={cn('flex-1 text-sm font-medium truncate', sel ? 'text-brand-600 dark:text-brand-400' : 'text-surface-800')}>
                        {plan.display_name}
                      </span>
                      <span className="text-[10px] text-surface-500 tabular-nums shrink-0">{fc}/{totalCount}</span>
                      {isDraftId(plan.id) ? (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-amber-50 dark:bg-amber-500/15 text-amber-600 dark:text-amber-400 font-medium shrink-0">Draft</span>
                      ) : !plan.is_active ? (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-surface-300/40 text-surface-500 font-medium shrink-0">Archived</span>
                      ) : (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-md badge-emerald font-medium shrink-0">Active</span>
                      )}
                      {sel && <Check size={14} className="text-brand-600 dark:text-brand-400 shrink-0" />}
                    </button>
                  );
                })
              )}
            </div>

            {/* New Plan */}
            <div className="border-t border-overlay/[0.04] p-3">
              {showCreate ? (
                <div className="flex items-center gap-2 animate-fade-in">
                  <input
                    type="text"
                    placeholder="Plan name…"
                    value={createName}
                    onChange={e => setCreateName(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') handleCreateDraft();
                      if (e.key === 'Escape') { setShowCreate(false); setCreateName(''); }
                    }}
                    className="flex-1 px-3 py-2 text-sm bg-surface-50/50 border border-overlay/[0.06] rounded-lg text-surface-900 placeholder-surface-500 focus:outline-none focus:border-brand-500/40 focus:ring-2 focus:ring-brand-500/20"
                    autoFocus
                  />
                  <button
                    onClick={handleCreateDraft}
                    disabled={!createName.trim()}
                    className="btn-primary text-xs !py-2 !px-3"
                  >
                    <Plus size={14} /> Create
                  </button>
                  <button
                    onClick={() => { setShowCreate(false); setCreateName(''); }}
                    className="p-1.5 rounded-lg text-surface-500 hover:text-surface-700 hover:bg-overlay/[0.04] transition-colors"
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setShowCreate(true)}
                  className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm font-medium text-brand-600 dark:text-brand-400 hover:bg-brand-500/10 transition-colors"
                >
                  <Plus size={15} /> New Plan
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Editor ──────────────────────────────────────────────── */}
      {!selectedId || !draft ? (
        <div className="glass-panel py-20 flex flex-col items-center justify-center">
          <div className="w-12 h-12 rounded-2xl bg-surface-200/50 flex items-center justify-center mb-4">
            <Layers size={22} className="text-surface-500" />
          </div>
          <p className="text-surface-600 text-sm">Select a plan to start configuring</p>
          <p className="text-surface-500 text-xs mt-1">or create a new one above</p>
        </div>
      ) : (
        <div className="glass-panel overflow-hidden animate-slide-up">
          {/* Plan identity strip */}
          <div className="flex items-center gap-4 px-6 py-4 border-b border-overlay/[0.04] bg-surface-50/30">
            <span className="w-3.5 h-3.5 rounded-full shrink-0 ring-2 ring-surface-0 shadow-sm" style={{ backgroundColor: draft.color }} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2.5">
                <h2 className="text-base font-semibold text-surface-900 truncate">{draft.display_name || 'Untitled Plan'}</h2>
                {isCurrentDraft ? (
                  <span className="text-[10px] px-2 py-0.5 rounded-md font-semibold bg-amber-50 dark:bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/20">
                    Draft — not yet saved
                  </span>
                ) : (
                  <span className={cn(
                    'text-[10px] px-2 py-0.5 rounded-md font-semibold',
                    draft.is_active ? 'bg-emerald-50 dark:bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20' : 'bg-surface-300/40 text-surface-500',
                  )}>
                    {draft.is_active ? 'Active' : 'Archived'}
                  </span>
                )}
                {tenantCount > 0 && <span className="text-[11px] text-surface-500">{tenantCount} tenant{tenantCount !== 1 ? 's' : ''}</span>}
              </div>
              <span className="text-xs text-surface-500 font-mono">{draft.name}</span>
            </div>
            <div className="text-right shrink-0">
              <div className="text-lg font-bold text-surface-900 tabular-nums">
                {draft.price_per_seat_monthly === 0 ? 'Free' : `$${(draft.price_per_seat_monthly / 100).toFixed(2)}`}
              </div>
              {draft.price_per_seat_monthly > 0 && <div className="text-[11px] text-surface-500">per seat / month</div>}
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-overlay/[0.04] px-2">
            {TABS.map(tab => {
              const Icon = tab.icon;
              const active = activeTab === tab.key;
              return (
                <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                  className={cn(
                    'flex items-center gap-2 px-4 py-3 text-sm font-medium transition-all relative',
                    active ? 'text-brand-600 dark:text-brand-400' : 'text-surface-500 hover:text-surface-700',
                  )}>
                  <Icon size={15} />
                  {tab.label}
                  {tab.key === 'features' && (
                    <span className={cn(
                      'text-[10px] px-1.5 py-0.5 rounded-md font-semibold tabular-nums',
                      active ? 'bg-brand-500/15 text-brand-600 dark:text-brand-400' : 'bg-surface-200/60 text-surface-500',
                    )}>
                      {enabledCount}/{totalCount}
                    </span>
                  )}
                  {active && <span className="absolute bottom-0 inset-x-2 h-[2px] bg-brand-500 rounded-full" />}
                </button>
              );
            })}
          </div>

          {/* Content */}
          <div className="p-6">
            {activeTab === 'general' && <GeneralTab draft={draft} upd={upd} tenantCount={tenantCount} />}
            {activeTab === 'pricing' && <PricingTab draft={draft} upd={upd} selectedId={selectedId} isCurrentDraft={isCurrentDraft} setSaveStatus={setSaveStatus} />}
            {activeTab === 'features' && (
              <FeaturesTab features={features} setFeatures={setFeatures}
                featureLimits={featureLimits} setFeatureLimits={setFeatureLimits}
                setSaveStatus={setSaveStatus} enabledCount={enabledCount} totalCount={totalCount} />
            )}
          </div>
        </div>
      )}

      {/* ── Info Note ────────────────────────────────────────────── */}
      <div className="flex items-start gap-2.5 px-4 py-3 rounded-xl bg-surface-200/30 border border-overlay/[0.04]">
        <Info size={14} className="text-surface-500 mt-0.5 shrink-0" />
        <p className="text-xs text-surface-500 leading-relaxed">
          New plans are created as <span className="text-amber-600 dark:text-amber-400 font-medium">drafts</span> and can be freely discarded before saving.
          Once saved to the database, a plan cannot be deleted — only <span className="text-surface-700 font-medium">archived</span>.
          Archived plans are hidden from tenant-facing pages but their data is preserved.
        </p>
      </div>

      {/* Pricing Assistant modal — mounted at the page root, focused
          on the currently-selected plan. Conversations live in
          PlanBuilder state (keyed by plan.name) so closing + reopening
          the modal, or switching between plans, preserves each plan's
          chat history. Apply writes monthly/annual cents into the
          active draft via setDraft. */}
      {assistantOpen && (
        <PricingAssistantPanel
          context={assistantContext}
          messages={assistantContext ? (conversations[assistantContext.id] ?? []) : []}
          onMessagesChange={handleConversationChange}
          onReset={handleConversationReset}
          onApply={handleAssistantApply}
          onClose={() => setAssistantOpen(false)}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// General Tab
// ═══════════════════════════════════════════════════════════════════════════════

function GeneralTab({ draft, upd, tenantCount }: { draft: PlanDraft; upd: (p: Partial<PlanDraft>) => void; tenantCount: number }) {
  return (
    <div className="max-w-2xl space-y-5">
      <div className="grid grid-cols-2 gap-4">
        <FField label="Display Name" hint="Public-facing plan name">
          <input type="text" value={draft.display_name} onChange={e => upd({ display_name: e.target.value })}
            className="input-field" placeholder="e.g. Pro, ExpoPlan" />
        </FField>
        <FField label="Slug" hint={tenantCount > 0 ? `Locked — ${tenantCount} tenant(s)` : 'Auto-generated identifier'}>
          <input type="text" value={draft.name} onChange={e => upd({ name: e.target.value })}
            readOnly={tenantCount > 0}
            className={cn('input-field font-mono text-sm', tenantCount > 0 && 'opacity-40 cursor-not-allowed')} />
        </FField>
      </div>

      <FField label="Description" hint="Tagline for billing and upgrade pages">
        <input type="text" value={draft.description} onChange={e => upd({ description: e.target.value })}
          className="input-field" placeholder="e.g. For growing teams that need collaboration" />
      </FField>

      <div className="grid grid-cols-2 gap-4">
        <FField label="Brand Color">
          <div className="flex items-center gap-3">
            <input type="color" value={draft.color} onChange={e => upd({ color: e.target.value })}
              className="w-10 h-10 rounded-xl border border-overlay/[0.06] cursor-pointer bg-transparent p-0.5" />
            <input type="text" value={draft.color} onChange={e => upd({ color: e.target.value })}
              className="input-field flex-1 font-mono text-sm uppercase" maxLength={7} />
            <span className="text-xs px-2.5 py-1 rounded-full font-semibold text-white shadow-sm" style={{ backgroundColor: draft.color }}>
              {draft.display_name || 'Plan'}
            </span>
          </div>
        </FField>
        <FField label="Rank" hint="Sort order (0 = lowest tier)">
          <input type="number" value={draft.rank} onChange={e => upd({ rank: parseInt(e.target.value) || 0 })}
            className="input-field" min={0} />
        </FField>
      </div>

      {/* Status toggle */}
      <div className="pt-4 border-t border-overlay/[0.04]">
        <label className="flex items-center justify-between cursor-pointer group">
          <div>
            <div className="text-sm font-medium text-surface-800">Plan Status</div>
            <div className="text-xs text-surface-500 mt-0.5">
              {draft.is_active ? 'Visible in provisioning and billing' : 'Hidden from all tenant-facing pages'}
            </div>
          </div>
          <Toggle checked={draft.is_active} onChange={v => upd({ is_active: v })} />
        </label>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Pricing & Limits Tab
// ═══════════════════════════════════════════════════════════════════════════════

function PricingTab({ draft, upd, selectedId, isCurrentDraft, setSaveStatus }: {
  draft: PlanDraft;
  upd: (p: Partial<PlanDraft>) => void;
  selectedId: string | null;
  isCurrentDraft: boolean;
  setSaveStatus: (s: SaveStatus) => void;
}) {
  return (
    <div className="max-w-3xl space-y-8">
      {/* Pricing */}
      <section>
        <SHead icon={DollarSign} title="Pricing" sub="Per-seat pricing and Stripe configuration" />
        <div className="grid grid-cols-2 gap-4 mt-4">
          <FField label="Monthly (per seat)" hint={`= ${draft.price_per_seat_monthly} ¢`}>
            <div className="relative">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-surface-500 text-xs font-mono">$</span>
              <input type="number" step="0.01" value={draft.price_per_seat_monthly === 0 ? '0' : (draft.price_per_seat_monthly / 100).toFixed(2).replace(/\.?0+$/, '') || '0'}
                onChange={e => upd({ price_per_seat_monthly: Math.round((parseFloat(e.target.value) || 0) * 100) })}
                className="input-field !pl-8 tabular-nums" min={0} placeholder="0.00" />
            </div>
          </FField>
          <FField label="Annual (per seat)" hint={`= ${draft.price_per_seat_annual} ¢`}>
            <div className="relative">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-surface-500 text-xs font-mono">$</span>
              <input type="number" step="0.01" value={draft.price_per_seat_annual === 0 ? '0' : (draft.price_per_seat_annual / 100).toFixed(2).replace(/\.?0+$/, '') || '0'}
                onChange={e => upd({ price_per_seat_annual: Math.round((parseFloat(e.target.value) || 0) * 100) })}
                className="input-field !pl-8 tabular-nums" min={0} placeholder="0.00" />
            </div>
          </FField>
          <FField label="Stripe Price ID (Monthly)" hint="Auto-managed">
            <input type="text" value={draft.stripe_price_id_monthly}
              readOnly
              className="input-field font-mono text-xs bg-surface-50 dark:bg-surface-800/50 text-surface-400 cursor-not-allowed" placeholder="Auto-created on save" />
          </FField>
          <FField label="Stripe Price ID (Annual)" hint="Auto-managed">
            <input type="text" value={draft.stripe_price_id_annual}
              readOnly
              className="input-field font-mono text-xs bg-surface-50 dark:bg-surface-800/50 text-surface-400 cursor-not-allowed" placeholder="Auto-created on save" />
          </FField>
          {/* Manual Stripe sync button */}
          {selectedId && !isCurrentDraft && (draft.price_per_seat_monthly > 0 || draft.price_per_seat_annual > 0) && (
            <div className="col-span-2">
              <button
                type="button"
                onClick={async () => {
                  setSaveStatus('saving');
                  const result = await syncB2BPlanStripe(selectedId, {
                    name: draft.name,
                    display_name: draft.display_name,
                    price_per_seat_monthly: draft.price_per_seat_monthly,
                    price_per_seat_annual: draft.price_per_seat_annual,
                  });
                  if (result) {
                    upd({
                      stripe_price_id_monthly: result.stripe_price_id_monthly,
                      stripe_price_id_annual: result.stripe_price_id_annual,
                    });
                    setSaveStatus('saved');
                    setTimeout(() => setSaveStatus('idle'), 2500);
                  } else {
                    setSaveStatus('error');
                    setTimeout(() => setSaveStatus('idle'), 3000);
                  }
                }}
                className="flex items-center gap-1.5 text-xs font-medium text-brand-500 hover:text-brand-600 transition-colors"
              >
                <RotateCcw size={12} /> Sync to Stripe
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Limits */}
      <section>
        <SHead icon={Sliders} title="Usage Limits" sub="Capacity thresholds for this plan" />
        <div className="mt-4 rounded-xl border border-overlay/[0.06] overflow-hidden divide-y divide-overlay/[0.04]">
          <LimitRow icon={Users} label="Max Users" desc="Seat limit"
            value={draft.max_users} unlimited={draft.max_users === null}
            onChange={v => upd({ max_users: v })} onToggle={on => upd({ max_users: on ? null : 10 })} />
          <LimitRow icon={MapIcon} label="Trips / Month" desc="Trip creation cap"
            value={draft.max_trips_per_month} unlimited={draft.max_trips_per_month === null}
            onChange={v => upd({ max_trips_per_month: v })} onToggle={on => upd({ max_trips_per_month: on ? null : 100 })} />
          <LimitRow icon={HardDrive} label="Storage" desc="File storage" suffix="MB"
            value={draft.max_storage_mb} unlimited={false} hideToggle
            onChange={v => upd({ max_storage_mb: v as number })} onToggle={() => {}} />
          <LimitRow icon={Mail} label="Campaigns / Month" desc="Email send limit"
            value={draft.max_campaigns_per_month} unlimited={draft.max_campaigns_per_month === -1}
            onChange={v => upd({ max_campaigns_per_month: v as number })} onToggle={on => upd({ max_campaigns_per_month: on ? -1 : 5 })} />
          <LimitRow icon={Send} label="Recipients / Campaign" desc="Per-campaign cap" hideToggle
            value={draft.max_campaign_recipients} unlimited={false}
            onChange={v => upd({ max_campaign_recipients: v as number })} onToggle={() => {}} />
          <LimitRow icon={Layers} label="B2C Plan Tiers" desc="Max plan tiers tenant can create"
            value={draft.max_b2c_plans} unlimited={draft.max_b2c_plans === -1}
            onChange={v => upd({ max_b2c_plans: v as number })} onToggle={on => upd({ max_b2c_plans: on ? -1 : 4 })} />
          <LimitRow icon={Gift} label="B2C Free Plans" desc="Max $0 plans tenant can offer"
            value={draft.max_b2c_free_plans} unlimited={draft.max_b2c_free_plans === -1}
            onChange={v => upd({ max_b2c_free_plans: v as number })} onToggle={on => upd({ max_b2c_free_plans: on ? -1 : 1 })} />
        </div>
      </section>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Features Tab
// ═══════════════════════════════════════════════════════════════════════════════

function FeaturesTab({ features, setFeatures, featureLimits, setFeatureLimits, setSaveStatus, enabledCount, totalCount }: {
  features: Record<string, boolean>;
  setFeatures: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  featureLimits: Record<string, number | null>;
  setFeatureLimits: React.Dispatch<React.SetStateAction<Record<string, number | null>>>;
  setSaveStatus: (s: SaveStatus) => void;
  enabledCount: number; totalCount: number;
}) {
  const setAll = (v: boolean) => {
    const m: Record<string, boolean> = {};
    for (const k of ALL_FEATURE_KEYS) m[k] = v;
    setFeatures(m); setSaveStatus('idle');
  };

  // Group features by category within each tier, sorted alphabetically by label
  const groupByCategory = (keys: string[]) => {
    const groups: Record<string, string[]> = {};
    for (const k of keys) {
      const cat = FEATURE_META[k]?.category;
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(k);
    }
    // Sort keys within each category alphabetically by label
    for (const cat of Object.keys(groups)) {
      groups[cat].sort((a, b) => (FEATURE_META[a]?.label || '').localeCompare(FEATURE_META[b]?.label || ''));
    }
    return Object.entries(groups).sort((a, b) => a[0].localeCompare(b[0]));
  };

  const appCategories = groupByCategory(APP_FEATURE_KEYS);
  const platformCategories = groupByCategory(PLATFORM_FEATURE_KEYS);

  const renderCategoryBlock = (cat: string, keys: string[]) => {
    const catOn = keys.filter(k => features[k]).length;
    return (
      <div key={cat} className="rounded-xl border border-overlay/[0.06] overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2.5 bg-surface-50/40">
          <span className="text-[11px] font-semibold text-surface-500 uppercase tracking-wider">{cat}</span>
          <span className="text-[10px] font-medium text-surface-500 tabular-nums">{catOn}/{keys.length}</span>
        </div>
        <div className="divide-y divide-overlay/[0.04]">
          {keys.map(key => {
            const meta = FEATURE_META[key];
            const on = features[key] ?? false;
            return (
              <div key={key}
                className="flex items-center justify-between gap-4 px-4 py-3 hover:bg-overlay/[0.04] transition-colors cursor-pointer"
                onClick={() => { setFeatures(prev => ({ ...prev, [key]: !on })); setSaveStatus('idle'); }}
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-surface-800">{meta.label}</div>
                  <div className="text-xs text-surface-500 mt-0.5">{meta.description}</div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  {/* Limit input for features that support it */}
                  {meta.hasLimit && on && (
                    <input
                      type="number"
                      min={0}
                      placeholder="∞"
                      value={featureLimits[key] ?? ''}
                      onClick={e => e.stopPropagation()}
                      onChange={e => {
                        e.stopPropagation();
                        setFeatureLimits(prev => ({
                          ...prev,
                          [key]: e.target.value === '' ? null : parseInt(e.target.value, 10),
                        }));
                        setSaveStatus('idle');
                      }}
                      className="w-16 text-center text-xs input-field py-1 px-1"
                      title="Max limit (empty = unlimited)"
                    />
                  )}
                  <Toggle checked={on} onChange={() => { setFeatures(prev => ({ ...prev, [key]: !on })); setSaveStatus('idle'); }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const appEnabled = APP_FEATURE_KEYS.filter(k => features[k]).length;
  const platEnabled = PLATFORM_FEATURE_KEYS.filter(k => features[k]).length;

  return (
    <div>
      {/* Summary bar */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-4">
          <div className="flex items-baseline gap-1.5">
            <span className="text-2xl font-bold text-surface-900 tabular-nums">{enabledCount}</span>
            <span className="text-sm text-surface-500">of {totalCount} enabled</span>
          </div>
          <div className="w-28 h-1.5 rounded-full bg-surface-300/50 overflow-hidden">
            <div className="h-full rounded-full bg-brand-500 transition-all duration-500 ease-out"
              style={{ width: `${(enabledCount / totalCount) * 100}%` }} />
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setAll(true)} className="text-xs font-medium text-brand-600 dark:text-brand-400 hover:text-brand-300 px-2.5 py-1.5 rounded-lg hover:bg-brand-500/10 transition-colors">
            Enable All
          </button>
          <span className="text-surface-400">·</span>
          <button onClick={() => setAll(false)} className="text-xs font-medium text-surface-500 hover:text-surface-700 px-2.5 py-1.5 rounded-lg hover:bg-overlay/[0.04] transition-colors">
            Disable All
          </button>
        </div>
      </div>

      {/* Side-by-side App + Platform columns */}
      <div className="grid grid-cols-2 gap-6 items-start">
        {/* App / B2C Features */}
        <div className="space-y-3">
          <div className="flex items-center gap-3 mt-1 mb-2">
            <span className="text-xs font-bold text-surface-600 uppercase tracking-wider">App / B2C Features</span>
            <span className="text-[10px] text-surface-500">{appEnabled}/{APP_FEATURE_KEYS.length}</span>
            <div className="flex-1 border-t border-overlay/[0.06]" />
          </div>
          {appCategories.map(([cat, keys]) => renderCategoryBlock(cat, keys))}
        </div>

        {/* Platform Features */}
        <div className="space-y-3">
          <div className="flex items-center gap-3 mt-1 mb-2">
            <span className="text-xs font-bold text-surface-600 uppercase tracking-wider">Platform Features</span>
            <span className="text-[10px] text-surface-500">{platEnabled}/{PLATFORM_FEATURE_KEYS.length}</span>
            <div className="flex-1 border-t border-overlay/[0.06]" />
          </div>
          {platformCategories.map(([cat, keys]) => renderCategoryBlock(cat, keys))}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Shared Components
// ═══════════════════════════════════════════════════════════════════════════════

function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button type="button" role="switch" aria-checked={checked}
      onClick={e => { e.stopPropagation(); onChange(!checked); }}
      className={cn(
        'relative w-10 h-[22px] rounded-full shrink-0 transition-colors duration-200',
        checked ? 'bg-brand-500' : 'bg-surface-300',
      )}>
      <span className={cn(
        'absolute top-[3px] left-[3px] w-4 h-4 rounded-full bg-white shadow-sm transition-transform duration-200',
        checked && 'translate-x-[18px]',
      )} />
    </button>
  );
}

function SHead({ icon: Icon, title, sub }: { icon: typeof DollarSign; title: string; sub: string }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-8 h-8 rounded-lg bg-surface-200/50 flex items-center justify-center">
        <Icon size={15} className="text-surface-600" />
      </div>
      <div>
        <h3 className="text-sm font-semibold text-surface-800">{title}</h3>
        <p className="text-xs text-surface-500">{sub}</p>
      </div>
    </div>
  );
}

function FField({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-sm font-medium text-surface-700 mb-1.5">{label}</label>
      {children}
      {hint && <div className="text-[11px] text-surface-500 mt-1.5">{hint}</div>}
    </div>
  );
}

function LimitRow({ icon: Icon, label, desc, value, unlimited, suffix, hideToggle, onChange, onToggle }: {
  icon: typeof Users; label: string; desc: string;
  value: number | null; unlimited: boolean;
  onChange: (v: number | null) => void; onToggle: (on: boolean) => void;
  suffix?: string; hideToggle?: boolean;
}) {
  return (
    <div className="flex items-center gap-4 px-4 py-3.5">
      <div className="w-8 h-8 rounded-lg bg-surface-200/40 flex items-center justify-center shrink-0">
        <Icon size={15} className="text-surface-600" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-surface-800">{label}</div>
        <div className="text-xs text-surface-500">{desc}</div>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        {unlimited ? (
          <span className="flex items-center gap-1.5 text-sm font-medium text-brand-600 dark:text-brand-400">
            <InfinityIcon size={16} /> Unlimited
          </span>
        ) : (
          <div className="flex items-center gap-1.5">
            <input type="number" value={value ?? ''} onChange={e => onChange(parseInt(e.target.value) || 0)}
              className="w-24 px-3 py-2 text-sm text-right bg-surface-50/50 border border-overlay/[0.06] rounded-lg text-surface-900 tabular-nums focus:outline-none focus:border-brand-500/40 focus:ring-2 focus:ring-brand-500/20"
              min={0} />
            {suffix && <span className="text-xs text-surface-500 font-medium">{suffix}</span>}
          </div>
        )}
        {!hideToggle && (
          <button onClick={() => onToggle(!unlimited)}
            className={cn(
              'text-xs font-medium px-2.5 py-1.5 rounded-lg border transition-all',
              unlimited
                ? 'badge-brand border-brand-500/20 hover:bg-brand-500/15'
                : 'text-surface-500 border-overlay/[0.06] hover:bg-overlay/[0.04] hover:text-surface-700',
            )}>
            {unlimited ? 'Set Limit' : 'Unlimited'}
          </button>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Kickoff message builder for the Pricing Assistant — platform B2B
// variant. Visible to the admin in the chat thread as the first user
// turn, so the agent has full context (current price, seat + storage
// limits, feature counts split B2C vs B2B, full label list).
// ─────────────────────────────────────────────────────────────────────
function buildPlatformKickoff(args: {
  display_name:   string;
  monthly_usd:    number;
  annual_usd:     number;
  max_users:      number | null;
  storage_mb:     number;
  b2c_enabled:    number;
  b2c_total:      number;
  b2b_enabled:    number;
  b2b_total:      number;
  feature_labels: string[];
}): string {
  const priceLine = args.monthly_usd === 0 && args.annual_usd === 0
    ? 'Currently priced as **Free**.'
    : `Currently priced at **$${args.monthly_usd.toFixed(2)}/mo, $${args.annual_usd.toFixed(0)}/yr**.`;

  const usersLine = args.max_users === null
    ? 'unlimited users'
    : `max ${args.max_users} user${args.max_users === 1 ? '' : 's'}`;

  const storageLine = args.storage_mb >= 1000
    ? `${(args.storage_mb / 1000).toFixed(args.storage_mb % 1000 === 0 ? 0 : 1)} GB storage`
    : `${args.storage_mb} MB storage`;

  const featureList = args.feature_labels.length > 0
    ? args.feature_labels.map(f => `- ${f}`).join('\n')
    : '_(no plan-gated features currently enabled)_';

  return [
    `I'm pricing the **${args.display_name}** plan.`,
    '',
    priceLine,
    `Limits: ${usersLine}, ${storageLine}.`,
    `Feature coverage: ${args.b2c_enabled}/${args.b2c_total} B2C · ${args.b2b_enabled}/${args.b2b_total} B2B.`,
    '',
    'Enabled plan-gated features:',
    featureList,
    '',
    'Suggest a price for this plan and compare with the top 3 closest competitors at the feature level. Explain why this price is right.',
  ].join('\n');
}
